import { Breadcrumb, Button, Label, TextInput, Card } from "flowbite-react";
import { useEffect, useRef, useState, type FC } from "react";
import { HiHome, HiChevronDown, HiCurrencyDollar } from "react-icons/hi";
import NavbarSidebarLayout from "../layouts/navbar-sidebar";
import { AddDealModal, AllDealsTable, Pagination } from "../components/dealsList";
import { useLocation, useNavigate } from "react-router";
import { useAppContext } from "../context/AppContext";
import Select from "react-select";
import { User } from "../services/user";
import { dealService } from "../services/main";

const DealsPage: FC = function () {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<{ value: any; label: any }[]>([]);
  const [selectedStage, setSelectedStage] = useState<string>("");
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const location = useLocation();
  const stateData = location.state;
  const { deals, setDeals, companies } = useAppContext();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const dropdownRef = useRef(null) as any; // Reference for dropdown

  // Fetch users for filter
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userService = new User("/users");
        const response = await userService.getAllUsers();
        setUsers(response.data.users || response.data || []);
      } catch (error) {
        console.error("Failed to fetch users", error);
      }
    };
    fetchUsers();
  }, []);

  const toggleFilterDropdown = () => {
    setIsFilterOpen((prev) => !prev);
  };

  const handleClickOutside = (event: any) => {
    // Check if click happened outside dropdown
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsFilterOpen(false); // Close the dropdown
    }
  };

  useEffect(() => {
    // Add event listener on document
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Cleanup the event listener
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const [useElasticsearch, setUseElasticsearch] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);

  // Use Elasticsearch search if enabled and search term exists
  useEffect(() => {
    const performSearch = async () => {
      if (searchTerm && searchTerm.trim().length > 0) {
        if (useElasticsearch) {
          setSearching(true);
          try {
            const response = await dealService.searchDeals(searchTerm.trim(), 100, 0);
            const results = response.data.deals || [];
            // If Elasticsearch returns results, use them
            if (results.length > 0) {
              setSearchResults(results);
            } else {
              // Elasticsearch returned no results (either no matches or unavailable)
              // Fall back to local search
              setUseElasticsearch(false);
              setSearchResults([]);
            }
          } catch (error) {
            // Elasticsearch failed, fall back to local search
            console.log("Elasticsearch unavailable, using local search");
            setUseElasticsearch(false);
            setSearchResults([]);
          } finally {
            setSearching(false);
          }
        } else {
          // Already using local search - no need to call API
          setSearchResults([]);
        }
      } else {
        // Empty search term - show all deals
        setSearchResults([]);
        setUseElasticsearch(true); // Reset to try Elasticsearch next time
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, useElasticsearch]);

  // Filter deals BEFORE pagination
  const filteredDeals = (() => {
    // If no search term, return all deals (after applying other filters)
    if (!searchTerm || searchTerm.trim().length === 0) {
      return deals.filter((deal: any) => {
        // Account Manager filter
        if (selectedUsers.length > 0) {
          const matchesUser = selectedUsers.some(u => deal.userId === u.value);
          if (!matchesUser) return false;
        }

        // Stage filter
        if (selectedStage) {
          if (deal.stage !== selectedStage) return false;
        }

        // Company filter
        if (selectedCompany) {
          if (deal.companyId !== Number(selectedCompany)) return false;
        }

        return true;
      });
    }

    // Start with deals to filter
    let dealsToFilter = deals;
    
    // If using Elasticsearch and we have results, use those
    if (useElasticsearch && searchResults.length > 0) {
      dealsToFilter = searchResults;
    }
    
    // Apply search and filters
    const searchLower = searchTerm.toLowerCase().trim();
    return dealsToFilter.filter((deal: any) => {
      // Search filter - apply local search if not using Elasticsearch or Elasticsearch returned no results
      if (!useElasticsearch || searchResults.length === 0) {
        const matchesSearch = 
          (deal.dealName?.toLowerCase().includes(searchLower)) ||
          (deal.description?.toLowerCase().includes(searchLower)) ||
          (deal.stage?.toLowerCase().includes(searchLower)) ||
          (deal.remark?.toLowerCase().includes(searchLower)) ||
          (deal.company?.name?.toLowerCase().includes(searchLower)) ||
          (deal.amount?.toString().includes(searchLower)) ||
          // Search in unstructured data
          (deal.unstructuredData && typeof deal.unstructuredData === 'object' &&
            Object.entries(deal.unstructuredData).some(([k, v]: [string, any]) => 
              k.toLowerCase().includes(searchLower) || 
              String(v).toLowerCase().includes(searchLower)
            ));
        if (!matchesSearch) return false;
      }

      // Account Manager filter
      if (selectedUsers.length > 0) {
        const matchesUser = selectedUsers.some(u => deal.userId === u.value);
        if (!matchesUser) return false;
      }

      // Stage filter
      if (selectedStage) {
        if (deal.stage !== selectedStage) return false;
      }

      // Company filter
      if (selectedCompany) {
        if (deal.companyId !== Number(selectedCompany)) return false;
      }

      return true;
    });
  })();

  // Pagination AFTER filtering
  const totalItems = filteredDeals.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedDeals = filteredDeals.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedUsers, selectedStage, selectedCompany]);

  // Get unique stages for filter dropdown
  const uniqueStages = Array.from(new Set(deals.map((d: any) => d.stage).filter(Boolean))).sort();

  return (
    <>
      <NavbarSidebarLayout>
        <div className="px-4 pt-6 pb-8">
          {/* Header Section */}
          <div className="mb-6">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item onClick={() => navigate("/")}>
                <div className="flex items-center gap-x-3 cursor-pointer">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Home</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Deals</Breadcrumb.Item>
            </Breadcrumb>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <HiCurrencyDollar className="text-purple-600 dark:text-purple-400" />
                  All Deals
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Showing {filteredDeals.length} of {deals.length} deals
                  {searchTerm && ` matching "${searchTerm}"`}
                </p>
              </div>
            </div>
          </div>
          {/* Search and Actions */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="w-full sm:w-auto">
              <form>
                <Label htmlFor="users-search" className="sr-only">
                  Search
                </Label>
                <div className="relative lg:w-64 xl:w-96">
                  <TextInput
                    id="users-search"
                    name="users-search"
                    placeholder="Search deals by name, description, company, amount..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                  {searching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                  {searchTerm && !searching && (
                    <div className="absolute -bottom-6 left-0 text-xs text-gray-500 dark:text-gray-400">
                      {useElasticsearch ? "Searching with Elasticsearch..." : "Using local search"}
                    </div>
                  )}
                </div>
              </form>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <AddDealModal />
              <div className="relative">
                <Button 
                  color="gray" 
                  type="button" 
                  onClick={toggleFilterDropdown}
                  className="hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      className="h-4 w-4 mr-2 text-gray-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Filter
                    {(selectedUsers.length > 0 || selectedStage || selectedCompany) && (
                      <span className="ml-2 bg-blue-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                        {[
                          selectedUsers.length > 0 ? selectedUsers.length : 0,
                          selectedStage ? 1 : 0,
                          selectedCompany ? 1 : 0
                        ].reduce((a, b) => a + b, 0)}
                      </span>
                    )}
                    <HiChevronDown className="ml-1.5 h-5 w-5" />
                  </Button>
                  {(selectedUsers.length > 0 || selectedStage || selectedCompany) && (
                    <button
                      className="absolute -top-1 -right-1 text-gray-500 hover:text-red-600 bg-white dark:bg-gray-800 rounded-full w-5 h-5 flex items-center justify-center text-xs border border-gray-200 dark:border-gray-700"
                      onClick={() => {
                        setSelectedUsers([]);
                        setSelectedStage("");
                        setSelectedCompany("");
                      }}
                      title="Clear all filters"
                    >
                      &#10005;
                    </button>
                  )}
                  {isFilterOpen && (
                    <div
                      ref={dropdownRef}
                      className="absolute z-20 mt-2 right-0 bg-white dark:bg-gray-800 border-0 rounded-xl shadow-xl p-6 min-w-[300px] max-w-[400px]"
                    >
                      <div className="space-y-4">
                        <div>
                          <Label className="mb-2 block text-sm font-medium">Account Manager</Label>
                          <Select
                            options={users.map((u: any) => ({
                              value: u.id,
                              label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || `User #${u.id}`
                            }))}
                            value={selectedUsers}
                            onChange={(vals) => setSelectedUsers(vals as any[])}
                            isMulti
                            isClearable={true}
                            placeholder="Select account managers..."
                            className="w-full text-sm"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            styles={{
                              menuPortal: base => ({ ...base, zIndex: 9999 })
                            }}
                          />
                        </div>

                        <div>
                          <Label className="mb-2 block text-sm font-medium">Stage</Label>
                          <select
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            value={selectedStage}
                            onChange={(e) => setSelectedStage(e.target.value)}
                          >
                            <option value="">All Stages</option>
                            {uniqueStages.map((stage: string) => (
                              <option key={stage} value={stage}>
                                {stage}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <Label className="mb-2 block text-sm font-medium">Company</Label>
                          <select
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            value={selectedCompany}
                            onChange={(e) => setSelectedCompany(e.target.value)}
                          >
                            <option value="">All Companies</option>
                            {companies.map((company: any) => (
                              <option key={company.id} value={company.id}>
                                {company.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                          <Button
                            color="gray"
                            size="xs"
                            onClick={() => {
                              setSelectedUsers([]);
                              setSelectedStage("");
                              setSelectedCompany("");
                            }}
                            className="flex-1"
                          >
                            Clear All
                          </Button>
                          <Button
                            color="primary"
                            size="xs"
                            onClick={() => setIsFilterOpen(false)}
                            className="flex-1"
                          >
                            Apply
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Table Section */}
          <Card className="border-0 shadow-lg">
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden">
                  <AllDealsTable paginatedDeals={paginatedDeals} searchTerm={searchTerm} />
                </div>
              </div>
            </div>
          </Card>
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </NavbarSidebarLayout>
    </>
  );
};

export default DealsPage;
